/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import React, {
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  useMaestroDataSource, useTranslation, clearMaestroDataSourceCache,
} from '@michelin/central-provider';
import {
  AppBar, Panel, Tab, Tabs, useDialogs, AccountAvatar, Container,
} from '@michelin/acid-components';
import DataGrid, {
  SearchPanel, Column, Selection,
} from 'devextreme-react/data-grid';
import {
  Grid, useMediaQuery, useTheme,
} from '@material-ui/core';
import { cloneDeep } from 'lodash';
import {
  massUpdatelocationsList,
  massValidateCommercialCertificates,
  massValidateOverrides,
  useMassUpdateCommercialErsInformation,
  useMassUpdateCommercialLocationInformation,
  useMassUpdateCommercialMechanicalServices,
  useMassUpdateCommercialNetworks,
  useMassUpdateCommercialProducts,
  useMassUpdateCommercialTireServices,
  useMassUpdateOverrides,
} from 'hooks/useMassSTUpdate';
import {
  CommercialNetworkServices,
  CommercialTireServices,
  CommercialMechanicalServices,
  commercialMechanicalServicesLabels,
} from 'components/CommercialServices/CommercialServicesFields';
import CommercialNetworksPanel from 'components/CommercialServices/CommercialNetworksPanel';
import CommercialServicesPanel from 'components/CommercialServices/CommercialTireServicesPanel';
import CommercialMechanicalPanel from 'components/CommercialServices/CommercialMechanicalServicesPanel';
import { HoursOfOperation, ProviderLocation } from 'Types';
import ERSPanel from 'components/CommercialERS/ERSPanel';
import { CommercialERSInformation } from 'components/CommercialERS/CommercialERSInformationFields';
import { getDates } from 'components/HoursOfOperation/Types';
import {
  FOOTERED_CONTENT_AREA_HEIGHT, getKeys, getTimeString, sleep,
} from 'Utils';
import useUtils from 'hooks/useUtils';
import { useLocationBT } from 'hooks/useLocation';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import { PageTitle } from 'Utils/CanvasPageTitle';
import { LoadingForm } from 'components/LoadingForm/LoadingForm';
import LocationInformation from './LocationInformation';
import CommercialProductsPanel from './Products';
import { fieldLabel } from './Fields';
import { CommercialOverrides } from '../../CommercialOverrides/CommercialOverridesFields';
import { CommercialOverridesPanel } from '../../CommercialOverrides/CommercialOverridesPanel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

interface Props {
  action: 'edit' | 'view',
}

type ActionType = 'add' | 'remove' | 'all';

interface Context {
  isLoading: boolean,
  selectedCount: number,
  onSave: (type: ActionType, isDirty: boolean, values: any, reset: any) => void,
  onError: (error: any) => void,
}

function TabPanel(props: TabPanelProps): JSX.Element | null {
  if (props.index !== props.value) return null;
  return <div style={{ paddingTop: 12 }}>{props.children}</div>;
}

export const CommercialContext = React.createContext<Context | null>(null);

export function CommercialMassSTUpdate(props: Props) {
  const { btNumber, tab } = useParams<{ btNumber: string, tab: string }>();
  const locationQuery = useLocationBT(btNumber);
  const { t } = useTranslation();
  const history = useHistory();
  const disableErs = (!!locationQuery.data?.has_kad_locations && !locationQuery.data?.has_commercial_locations);
  const { convertFromUOM, getValuesByKey } = useUtils();
  const permissions = usePermissionsSPP();
  const { subTitle } = useUtils();
  const {
    errorDialog, messageDialog, confirmDialog, loadingDialog,
  } = useDialogs();
  const theme = useTheme();
  const [cacheReloads, setCacheReloads] = useState<number>(0);
  const tabletView = useMediaQuery(theme.breakpoints.down(960));
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [action, setAction] = useState<('edit' | 'view')>(props.action);
  const [loading, setLoading] = useState<boolean>(false);

  const countryCode = permissions.location?.customer_country.toLocaleLowerCase() || 'us';
  const stList = useMaestroDataSource({
    API: massUpdatelocationsList(btNumber),
    reqOptions: { per_page: 5000, filters: { reduced: 'true', types: 'commercial,tad,kad,oliver' } },
    cacheKey: 'commercial-mass-st-update',
  });
  const useSaveLocationInformation = useMassUpdateCommercialLocationInformation();
  const useSaveProducts = useMassUpdateCommercialProducts();
  const useSaveNetworks = useMassUpdateCommercialNetworks();
  const useSaveErsInformation = useMassUpdateCommercialErsInformation();
  const useSaveTireServices = useMassUpdateCommercialTireServices();
  const useSaveMechanicalServices = useMassUpdateCommercialMechanicalServices();
  const useSaveOverrides = useMassUpdateOverrides();

  const dataGrid = useRef<DataGrid>(null);

  function hasOverrides(location: ProviderLocation): boolean {
    return JSON.stringify(location.overrides || '').includes('true');
  }

  const renderAccount = (loc: ProviderLocation): any => {
    const name = (loc.ship_to.dba_name || loc.ship_to.name || '');
    const searchTerm = dataGrid?.current?.instance.getCombinedFilter()?.filterValue.trim();
    return (
      <>
        <AccountAvatar
          avatarPosition="none"
          name={(loc.ship_to.store_number && name.indexOf(`#${loc.ship_to.store_number}`) < 0 ? `${name} #${loc.ship_to.store_number}` : name)}
          accountType="ST"
          accountDisplay="short"
          size="normal"
          useHighlight
          searchTerm={searchTerm}
          accountNumbers={
            {
              shipTo: loc.ship_to.ship_to_number,
              mdm: loc.mdm?.site_id || undefined,
            }
          }
          addressDisplay="cityState"
          address={{
            address1: undefined,
            address2: undefined,
            city: loc.city,
            state: loc.state,
            zip: loc.zip,
          }}
          terminalId=""
        />
        {hasOverrides(loc) ? <span style={{ fontSize: '0.8em', opacity: 0.5 }}>(this location has overrides)</span> : null}
      </>
    );
  };

  function isHoursOfOperation(key: string): boolean {
    return (key === 'mechanical_hours_of_operation'
      || key === 'tire_services_hours_of_operation'
      || key === 'ers_mechanical_services.mechanical_hours_of_operation'
      || key === 'ers_mechanical_services.tire_services_hours_of_operation');
  }

  function getFlatObject(object: any) {
    function iter(item: any, p: any, path: any) {
      if (item && typeof item === 'object' && !isHoursOfOperation(p.join('.'))) {
        Object.keys(item).forEach((key) => {
          iter(item[key], p.concat(key), path);
        });
        return;
      }
      path[p.join('.')] = item;
    }

    const path = {};
    iter(object, [], path);
    return path;
  }

  const getHOODescription = (enabled: (string | JSX.Element)[], hoo: HoursOfOperation): (string | JSX.Element)[] => {
    const dates = getDates(countryCode);
    const closed = <span style={{ color: '#CCC', fontStyle: 'italic' }}>{t('Closed')}</span>;

    Object.entries(hoo).forEach(([day, value]) => {
      if (!hoo || !day) return;
      const date = dates.find((d) => d.key === day);
      if (!date) return;
      // @ts-ignore
      const status = getTimeString(value) || closed;
      enabled.push(
        <>
          <span style={{ width: 245, display: 'inline-block' }}>{t(date.label)}</span>
          <span style={{ width: 180, textAlign: 'center', display: 'inline-block' }}>{status}</span>
        </>,
      );
    });

    return enabled;
  };

  const getDescription = (cloneData: any): (string | JSX.Element)[] => {
    const enabled: (string | JSX.Element)[] = [];
    const flatObject = getFlatObject(cloneData);

    if (tab === 'tires-services' || tab === 'mechanical-services') {
      const propToReplace = new Map();
      propToReplace.set('mobile', 'Mobile');
      propToReplace.set('in_bay', 'In Bay');
      propToReplace.set('light_medium_duty', 'Lt/Md Duty');
      propToReplace.set('heavy_duty', 'Heavy Duty');
      propToReplace.set('trailers_greater_than_10000_lbs', 'Trailers >= 10,000 lbs');
      propToReplace.set('trailers_less_than_10000_lbs', 'Trailers < 10,000 lbs');
      propToReplace.set('authorized_service_of', 'Authorized service provider');

      Object.entries(flatObject).forEach(([key, value]) => {
        if (key === 'number_of_dedicated_mobile_mechanical_units') {
          enabled.push(`${t('# Dedicated Mobile/Mechanical Units')}: ${t(value === null ? 'unset' : `${value}`)}`);
          return;
        }
        const type = key.split('.').filter((prop) => propToReplace.has(prop)).map((prop) => t(propToReplace.get(prop) || '')).join(' ');
        const label = key.split('.').filter((prop) => !propToReplace.has(prop)).map((prop) => prop).join('.');
        enabled.push(`${t(fieldLabel(label))}${type ? ` ${t(type)}` : ''}: ${t(`${value}`)}`);
      });
    } else if (tab === 'overrides') {
      const data = cloneData as CommercialOverrides;
      getKeys(data.overrides).forEach((key) => {
        let changeDescription = t('No');
        if (data.overrides[key]) {
          let value = data.overrides_value[key] ? t('Yes') : t('No');
          if (key === 'service_tier_ranking') {
            value = `${data.overrides_value.service_tier_ranking} (${t(data.overrides_value.service_tier_ranking_reason)})`;
          }
          changeDescription = t('Overrides with {{value}}', { value });
        }
        enabled.push(
          <>
            {`${t(fieldLabel(`overrides.${key}`))}: `}
            <span style={{ fontWeight: 'bold' }}>{changeDescription}</span>
          </>,
        );
      });
    } else {
      Object.entries(flatObject).forEach(([key, value]) => {
        if (isHoursOfOperation(key)) {
          enabled.push(`${t(fieldLabel(key))}:`);
          getHOODescription(enabled, value as HoursOfOperation);
        } else if (key.includes('service_radius')) {
          enabled.push(`${t(fieldLabel(key))}: ${convertFromUOM(value, countryCode)}`);
        } else {
          enabled.push(`${t(fieldLabel(key))}: ${value}`);
        }
      });
    }
    return enabled;
  };

  const getDataToSave = (data: any, type: ActionType) => {
    getKeys(data).forEach((key) => {
      if (key === 'heavy_duty_tractor_oe_manufacturers' || key === 'heavy_duty_trailer_oe_manufacturers' || key === 'medium_duty_oe_manufacturers') {
        if (Array.isArray(data[key])) {
          const oldArray: string[] = data[key];
          data[key] = oldArray.filter((i: string) => i.trim() !== '' && i.trim() !== ':');
        }
      }
      if (typeof (data[key]) === 'object') {
        getDataToSave(data[key], type);
        if (Object.keys(data[key]).length === 0) delete data[key];
      } else {
        if (type === 'all') {
          if (key === 'heavy_duty_tractor_oe_manufacturers' || key === 'heavy_duty_trailer_oe_manufacturers' || key === 'medium_duty_oe_manufacturers') data[key] = [];
          else if (data[key] === undefined) data[key] = false as any;
        }
        if (key === 'number_of_dedicated_mobile_mechanical_units' && !data[key] && data[key] !== 0) {
          data[key] = (type === 'all' ? null : undefined) as any;
        }
        if (type === 'all') return;
        if (data[key] === undefined || Number.isNaN(data[key])) delete data[key];
        if (data[key] === false) delete data[key];
        if (data[key] === true && type === 'remove') data[key] = false as any;
      }
    });
  };

  const getDataToSaveNoNested = (data: any) => {
    const numericKeys = ['number_of_trucks'];
    if (data.pos_type === '') delete data.pos_type;
    getKeys(data).forEach((key) => {
      if (typeof (data[key]) === 'object' && data[key] !== null) {
        getDataToSaveNoNested(data[key]);
        if (Object.keys(data[key]).length === 0) delete data[key];
      }
      if (data[key] === undefined) delete data[key];
      if (numericKeys.includes(key.toString())) data[key] = parseFloat(data[key]);
    });
  };

  const save = async (isDirty: boolean, values: any, reset: any, type: string) => {
    let errorTitle = t('Location Information');
    try {
      if (!isDirty) return;

      setLoading(true);

      const objectToSave = {
        ship_to_numbers: selectedItems.map((item) => item.ship_to.ship_to_number),
        data: values,
      };

      switch (tab) {
        case 'location-information':
          errorTitle = t('Location Information');
          await useSaveLocationInformation.mutateAsync({ btNumber, data: objectToSave });
          // Refresh the Location Information tab to avoid bad data on hoo.
          setAction('view');
          setAction(props.action);
          break;
        case 'products':
          errorTitle = t('Products');
          await useSaveProducts.mutateAsync({ btNumber, data: objectToSave });
          break;
        case 'networks':
          errorTitle = t('Networks');
          await useSaveNetworks.mutateAsync({ btNumber, data: objectToSave });
          break;
        case 'ers-information':
          errorTitle = t('ERS Information');

          // Complete the services to save the ERS information correctly, not only the dirty info.
          if (Object.prototype.hasOwnProperty.call(objectToSave.data, 'ers_mechanical_services')) {
            objectToSave.data.ers_mechanical_services.ers_mechanical_services_provided = {
              brakes_repair: false,
              diagnostic_services: false,
              dot_inspection: false,
              electric_vehicle_charging: false,
              fuel_delivery: false,
              jump_starts: false,
              lights: false,
              lockouts: false,
              suspension_repair: false,
              towing: false,
              ...objectToSave.data.ers_mechanical_services.ers_mechanical_services_provided,
            };
          }

          await useSaveErsInformation.mutateAsync({ btNumber, data: objectToSave });
          break;
        case 'tires-services':
          errorTitle = t('Tires Services');
          await useSaveTireServices.mutateAsync({ btNumber, data: objectToSave });
          break;
        case 'mechanical-services':
          errorTitle = t('Mechanical Services');
          if (objectToSave.data) objectToSave.data.mode = type;
          await useSaveMechanicalServices.mutateAsync({ btNumber, data: objectToSave });
          break;
        case 'overrides':
          errorTitle = t('Mechanical Overrides');
          await useSaveOverrides.mutateAsync({ btNumber, data: objectToSave });
          clearMaestroDataSourceCache('commercial-mass-st-update');
          await sleep(1500);
          setCacheReloads(cacheReloads + 1);
          break;
        default:
          break;
      }

      if (dataGrid.current) {
        dataGrid.current.instance.clearSelection();
        dataGrid.current.instance.clearFilter();
      }

      setLoading(false);
      reset();
      history.push('list');
    } catch (error) {
      setLoading(false);
      errorDialog(
        t('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n'),
        t('Error saving {{tab_name}} Data.', {
          tab_name: t(errorTitle),
        }),
      );
    }
  };

  const getDataToSaveOverrides = (data: CommercialOverrides, type: ActionType) => {
    getKeys(data.overrides).forEach((key) => {
      if (key === 'service_tier_ranking') return;
      if (!data.overrides[key]) {
        if (type === 'all') data.overrides_value[key] = false;
        if (type === 'add' || type === 'remove') {
          delete data.overrides[key];
          delete data.overrides_value[key];
        }
      }
      if (data.overrides[key] && type === 'remove') {
        data.overrides[key] = false;
        data.overrides_value[key] = false;
      }
    });
    if (!data.overrides.service_tier_ranking) {
      if (type === 'all') {
        data.overrides_value.service_tier_ranking = '';
        data.overrides_value.service_tier_ranking_reason = '';
      } else if (type === 'add' || type === 'remove') {
        delete (data as any).overrides.service_tier_ranking;
        delete (data as any).overrides_value.service_tier_ranking;
        delete (data as any).overrides_value.service_tier_ranking_reason;
      }
    }
    if (data.overrides.service_tier_ranking && type === 'remove') {
      data.overrides.service_tier_ranking = false;
      data.overrides_value.service_tier_ranking = '';
      data.overrides_value.service_tier_ranking_reason = '';
    }
    delete (data as any).overrides_original;
  };

  const prevalidateMechanicalServices = async (cloneData: any) => {
    if (permissions.location?.customer_country === 'CA') return true;
    const hasBasicMechanicalBasicBrakeSerivce = JSON.stringify(cloneData.basic_mechanical?.basic_brake_service || null).includes('true');
    const hasLightMechanicalBasicBrakeSerivce = JSON.stringify(cloneData.light_mechanical?.basic_brake_service || null).includes('true');
    const hasBasicMechanicalDotAnnualInspection = JSON.stringify(cloneData.basic_mechanical?.dot_annual_inspection || null).includes('true');
    const hasLightMechanicalDotAnnualInspection = JSON.stringify(cloneData.light_mechanical?.dot_annual_inspection || null).includes('true');
    const hasLightMechanicalStateInspection = JSON.stringify(cloneData.light_mechanical?.state_inspection || null).includes('true');

    const hasSensibleServices = hasBasicMechanicalBasicBrakeSerivce
                                || hasLightMechanicalBasicBrakeSerivce
                                || hasBasicMechanicalDotAnnualInspection
                                || hasLightMechanicalDotAnnualInspection
                                || hasLightMechanicalStateInspection;

    if (!hasSensibleServices) return true;
    const finishLoading = loadingDialog(undefined, t('Validating required certificates for selected locations...'));
    const certificatesValidations = await massValidateCommercialCertificates({
      btNumber,
      data: { ship_to_numbers: selectedItems.map((item) => item.ship_to.ship_to_number) as string[] },
    });
    finishLoading();

    const missingCertificates = new Map<keyof typeof commercialMechanicalServicesLabels, number>();
    if (hasBasicMechanicalBasicBrakeSerivce) {
      const count = selectedItems.filter((item) => !certificatesValidations.basic_brake_service.includes(item.ship_to.ship_to_number)).length;
      if (count) missingCertificates.set('basic_mechanical.basic_brake_service', count);
    }

    if (hasLightMechanicalBasicBrakeSerivce) {
      const count = selectedItems.filter((item) => !certificatesValidations.basic_brake_service.includes(item.ship_to.ship_to_number)).length;
      if (count) missingCertificates.set('light_mechanical.basic_brake_service', count);
    }

    if (hasBasicMechanicalDotAnnualInspection) {
      const count = selectedItems.filter((item) => !certificatesValidations.dot_annual_inspection.includes(item.ship_to.ship_to_number)).length;
      if (count) missingCertificates.set('basic_mechanical.dot_annual_inspection', count);
    }

    if (hasLightMechanicalDotAnnualInspection) {
      const count = selectedItems.filter((item) => !certificatesValidations.dot_annual_inspection.includes(item.ship_to.ship_to_number)).length;
      if (count) missingCertificates.set('light_mechanical.dot_annual_inspection', count);
    }

    if (hasLightMechanicalStateInspection) {
      const count = selectedItems.filter((item) => !certificatesValidations.state_inspection.includes(item.ship_to.ship_to_number)).length;
      if (count) missingCertificates.set('light_mechanical.state_inspection', count);
    }

    if (missingCertificates.size === 0) return true;
    const keys = Array.from(missingCertificates.keys());
    const res = await confirmDialog(
      keys.map(
        (key) => t(
          '{{name}} won\'t be set on {{count}} locations since they don\'t have a contact with the required certificate.',
          { count: missingCertificates.get(key), name: t(commercialMechanicalServicesLabels[key]) },
        ),
      ).map((item) => <li>{item}</li>),
      t('Some services are not available for selected locations.'),
      t('Continue and apply changes when possible'),
    );
    return res;
  };

  const prevalidateOverrides = async (cloneData: any) => {
    const hasOverrides = cloneData?.overrides_value?.on_tire_locator || cloneData?.overrides_value?.on_mechanical_locator;
    if (!hasOverrides) return true;
    const shipToNumbers = selectedItems.map((item) => item.ship_to.ship_to_number) as string[];
    const finishLoading = loadingDialog(undefined, t('Validating overrides for selected locations...'));
    const overridesValidations = await massValidateOverrides({
      btNumber,
      data: {
        ship_to_numbers: shipToNumbers,
        data: { ...cloneData, overrides_original: undefined },
      },
    });
    finishLoading();

    let invalidOnTireLocatorCount = 0;
    if (cloneData?.overrides_value?.on_tire_locator) {
      invalidOnTireLocatorCount = shipToNumbers.filter((stNumber:string) => !overridesValidations.on_tire_locator.includes(stNumber)).length;
    }

    let invalidOnMechanicalLocatorCount = 0;
    if (cloneData?.overrides_value?.on_mechanical_locator) {
      invalidOnMechanicalLocatorCount = shipToNumbers.filter((stNumber:string) => !overridesValidations.on_mechanical_locator.includes(stNumber)).length;
    }

    if (invalidOnTireLocatorCount === 0 && invalidOnMechanicalLocatorCount === 0) return true;

    const msg = [];
    if (invalidOnTireLocatorCount) {
      msg.push(t(
        '{{name}} won\'t be set on {{count}} locations since they don\'t have a valid service address or GPS location set.',
        { count: invalidOnTireLocatorCount, name: t('On Tire Locator') },
      ));
    }

    if (invalidOnMechanicalLocatorCount) {
      msg.push(t(
        '{{name}} won\'t be set on {{count}} locations since they don\'t have a valid service address or GPS location set.',
        { count: invalidOnMechanicalLocatorCount, name: t('On Mechanical Locator') },
      ));
    }

    const res = await confirmDialog(
      msg.map((item) => <li>{item}</li>),
      t('Some overrides are not available for selected locations.'),
      t('Continue and apply changes when possible'),
    );
    return res;
  };

  const onConfirm = async (type: ActionType, isDirty: boolean, values: any, reset: any) => {
    const cloneData = cloneDeep(values);

    // Validations
    if (tab === 'mechanical-services' && type !== 'remove') {
      if (!await prevalidateMechanicalServices(cloneData)) return;
    }
    if (tab === 'overrides' && type !== 'remove') {
      if (!await prevalidateOverrides(cloneData)) return;
    }

    // Cleaning the object
    if (tab === 'location-information' || tab === 'ers-information') {
      getDataToSaveNoNested(cloneData);
    } else if (tab === 'overrides') {
      getDataToSaveOverrides(cloneData, type);
    } else {
      getDataToSave(cloneData, type);
    }

    const list = getDescription(cloneData);
    const message = [
      ...t("You're about to update selected data on {{count}} location(s).\nThis action can't be undone.", { count: selectedItems.length }).split('\n'),
      <br />,
      <b>{t('Selected location(s) will override the following data to their current list:')}</b>,
      ...list.map((s, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={`srv-${i}`}>
          &nbsp; &#9679; &nbsp;
          {s}
        </span>
      )),
    ];
    confirmDialog(
      message,
      t('Update data on {{count}} location(s)', { count: selectedItems.length }),
      t('Update data on {{count}} location(s)', { count: selectedItems.length }),
      t('Cancel'),
      () => save(isDirty, cloneData, reset, type),
    );
  };

  const validateData = (type: ActionType, isDirty: boolean, values: any, reset: any) => {
    if (selectedItems.length === 0) {
      return (messageDialog(
        t('Please select at least one location to update.'),
        t('No location selected'),
        t('understood'),
      ));
    }
    const michelinNewTiresCount = selectedItems.filter((item) => !!item.michelin_new_tires === false).length;
    if (tab === 'products' && michelinNewTiresCount > 0) {
      confirmDialog(
        t(
          '{{count}} Locations are not Michelin New Tire Dealers and X ONE Tires will not be updated with this selection.\nDo you wish to continue with the updates?',
          { count: michelinNewTiresCount },
        ).split('\n'),
        t('Update data on {{count}} location(s)', { count: selectedItems.length }),
        t('Accept'),
        t('Cancel'),
        () => onConfirm(type, isDirty, values, reset),
      );
    } else {
      onConfirm(type, isDirty, values, reset);
    }
    return null;
  };

  const contextValue: Context = {
    isLoading: useSaveLocationInformation.isLoading
      || useSaveProducts.isLoading
      || useSaveNetworks.isLoading
      || useSaveTireServices.isLoading
      || useSaveMechanicalServices.isLoading,
    selectedCount: selectedItems.length,
    onSave: validateData,
    onError: (errors: any) => {
      const errorMessages: string[] = getValuesByKey(errors, 'message');
      const message = (errorMessages.filter((m) => m !== '').length > 0 ? errorMessages : t('Please check the marked fields.').split('\n'));

      errorDialog(
        message,
        t('Error saving commercial data.'),
      );
    },
  };

  if (disableErs && tab === 'ers-information') history.push('/');

  return (
    <>
      <PageTitle
        title={t('Mass ST Update')}
        subtitle={subTitle}
        goHomeBtn
      />
      <Container style={{ marginBottom: 40 }} className={`${loading ? 'loading-container' : ''}`}>
        {loading && <LoadingForm />}
        <Grid container spacing={2}>
          {!tabletView && <Grid item sm={3} md={3} style={{ height: FOOTERED_CONTENT_AREA_HEIGHT }} />}
          <Grid item sm={3} md={3} style={!tabletView ? { position: 'fixed', paddingRight: 22 } : undefined}>
            <Panel>
              {useMemo(() => (
                <DataGrid
                  key="datagrid"
                  ref={dataGrid}
                  dataSource={stList.dataSource}
                  paging={{ pageSize: 10000 }}
                  style={{ height: FOOTERED_CONTENT_AREA_HEIGHT }}
                  onSelectionChanged={({ selectedRowKeys }) => {
                    setSelectedItems(selectedRowKeys || []);
                  }}
                >
                  <Selection mode="multiple" showCheckBoxesMode="always" />
                  <SearchPanel visible highlightSearchText={false} />
                  <Column
                    caption={t('ST Name')}
                    cellRender={({ row }) => renderAccount(row.data)}
                    allowSorting
                    sortOrder="asc"
                    allowSearch
                    calculateCellValue={
                      // eslint-disable-next-line max-len
                      (row: any) => `${row?.ship_to.dba_name || row?.ship_to.name} ${row?.ship_to?.ship_to_number} ${row?.mdm?.site_id} ${row?.city} ${row?.state} ${row?.zip} ${hasOverrides(row) ? 'overrides' : ''}`.trim()
                    }
                  />
                </DataGrid>
              ), [props.action, cacheReloads])}
            </Panel>
          </Grid>
          <Grid item sm={9} md={9}>
            <CommercialContext.Provider value={contextValue}>
              <Panel>
                <AppBar style={{ height: 48 }} color="primary" position="static">
                  <Tabs
                    value={tab}
                    onChange={(event, newValue: string) => {
                      if (disableErs && tab === 'ers-information') {
                        history.push('/');
                        return;
                      }
                      history.push(`/${btNumber}/massSTUpdate/commercial/${newValue}/list`);
                    }}
                    variant="scrollable"
                    scrollButtons="on"
                  >
                    <Tab label={t('Location Information')} value="location-information" />
                    <Tab label={t('Products')} value="products" />
                    <Tab label={t('Networks')} value="networks" />
                    {!disableErs && <Tab label={t('ERS Information')} value="ers-information" />}
                    <Tab label={t('Tire Services')} value="tires-services" />
                    <Tab label={t('Mechanical Services')} value="mechanical-services" />
                    {permissions.allowsAction('admin_features.update_commercial_overrides')
                      ? <Tab label={t('Overrides')} value="overrides" />
                      : null}
                  </Tabs>
                </AppBar>
                <TabPanel index="location-information" value={tab}>
                  <LocationInformation
                    action={action}
                    fromMassUpdate
                  />
                </TabPanel>
                <TabPanel index="products" value={tab}>
                  <CommercialProductsPanel
                    action={props.action}
                    fromMassUpdate
                  />
                </TabPanel>
                <TabPanel index="networks" value={tab}>
                  <CommercialNetworksPanel
                    networks={useMemo(() => ({} as CommercialNetworkServices), [tab])}
                    action={props.action}
                    fromMassUpdate
                  />
                </TabPanel>
                <TabPanel index="ers-information" value={tab}>
                  <ERSPanel
                    ersInformation={useMemo(() => ({} as CommercialERSInformation), [tab])}
                    action={props.action}
                    fromMassUpdate
                  />
                </TabPanel>
                <TabPanel index="tires-services" value={tab}>
                  <CommercialServicesPanel
                    services={useMemo(() => ({} as CommercialTireServices), [tab])}
                    action={props.action}
                    fromMassUpdate
                  />
                </TabPanel>
                <TabPanel index="mechanical-services" value={tab}>
                  <CommercialMechanicalPanel
                    services={useMemo(() => ({} as CommercialMechanicalServices), [tab])}
                    action={props.action}
                    fromMassUpdate
                  />
                </TabPanel>
                <TabPanel index="overrides" value={tab}>
                  <CommercialOverridesPanel
                    overrides={useMemo(() => ({
                      overrides: {
                        mcsn_member: false,
                        on_mechanical_locator: false,
                        on_tire_locator: false,
                        service_tier_ranking: false,
                      },
                      overrides_value: {},
                      overrides_original: {},
                    } as CommercialOverrides), [tab])}
                    action={props.action}
                    fromMassUpdate
                  />
                </TabPanel>
              </Panel>
            </CommercialContext.Provider>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
